// Pure money-state/presentation logic for the dashboard — no React, no
// router, no component imports — so it (and its unit tests) can be imported
// without pulling in the rest of the app's module graph. Dashboard.js
// renders from this; it must never itself contain the state→copy mapping.
import {
  fieldText,
  getFlowCopy,
  getTransactionFlowType,
  isDisputeStatusOpen,
  isProviderFundingConfirmed,
  resolveEscrowUiState,
} from '../components/transactionState';
import { VDark as D } from '../components/designTokens';

// Frontend-only heuristic to spot obvious test/dev transactions in activity
// lists so they don't carry the same visual weight as real user activity.
// No backend flag exists for this - see CLAUDE.md "UI/UX policy: Trust and
// honesty" - so this only de-emphasizes, it never hides/filters outright.
const TEST_DATA_PATTERN = /(^|[\s([-])test\b|\be2e\b/i;
export const isLikelyTestTransaction = (transaction) => {
  const text = `${transaction?.item_description || ''} ${transaction?.title || ''}`;
  return TEST_DATA_PATTERN.test(text);
};

export const getTransactionValue = (transaction) =>
  transaction?.item_price ?? transaction?.total ?? transaction?.amount ?? 0;

export const isUserBuyerForTransaction = (transaction, user) =>
  transaction?.buyer_user_id === user?.user_id ||
  (transaction?.buyer_email && user?.email && transaction.buyer_email.toLowerCase() === user.email.toLowerCase());

const emailMatches = (value, user) =>
  Boolean(value && user?.email && String(value).trim().toLowerCase() === String(user.email).trim().toLowerCase());

const normalizePhone = (value) => String(value || '').replace(/\D/g, '');

const phoneMatches = (transactionValue, user) => {
  const transactionPhone = normalizePhone(transactionValue);
  const userPhone = normalizePhone(user?.phone);
  return Boolean(transactionPhone && userPhone && transactionPhone === userPhone);
};

export const isUserSellerForTransaction = (transaction, user) =>
  transaction?.seller_user_id === user?.user_id ||
  (transaction?.seller_email && user?.email && transaction.seller_email.toLowerCase() === user.email.toLowerCase()) ||
  (transaction?.freelancer_email && user?.email && transaction.freelancer_email.toLowerCase() === user.email.toLowerCase());

export const isUserParticipantForTransaction = (transaction, user) =>
  Boolean(transaction && user && (
    isUserBuyerForTransaction(transaction, user) ||
    isUserSellerForTransaction(transaction, user) ||
    (transaction?.recipient_info && user?.email && transaction.recipient_info.toLowerCase() === user.email.toLowerCase()) ||
    phoneMatches(transaction?.buyer_phone, user) ||
    phoneMatches(transaction?.seller_phone, user) ||
    phoneMatches(transaction?.recipient_info, user)
  ));

const isExplicitDisputeCardParticipant = (transaction, user) =>
  Boolean(transaction && user && (
    transaction?.buyer_user_id === user?.user_id ||
    transaction?.seller_user_id === user?.user_id ||
    emailMatches(transaction?.buyer_email, user) ||
    emailMatches(transaction?.seller_email, user) ||
    emailMatches(transaction?.invited_email, user) ||
    emailMatches(transaction?.recipient_email, user) ||
    emailMatches(transaction?.recipient_info, user)
  ));

const normalizeStatus = (...values) => fieldText(...values).replace(/[_-]/g, ' ');

// Both the terminal check and the open check now delegate to
// transactionState.js's isDisputeStatusOpen — the ONE shared definition of
// "is this dispute status terminal" for the whole frontend, mirroring
// backend/services/dispute_state.py exactly. This file previously kept its
// own separate (status-only, by-then-correct) definition and
// transactionState.js's hasOpenDispute kept ANOTHER, broader one
// (RESOLVED/CLOSED/DISMISSED/CANCELLED/CANCELED) — two definitions that had
// already drifted apart once and could again. Deliberately does NOT read
// dispute.resolution or dispute.admin_decision: those are free-text/label
// fields set when a dispute is resolved, and the backend's appeal route
// (routes/disputes.py::appeal_dispute) does not clear them when flipping
// status back to "Appealed" — so an appealed dispute can still carry
// resolution text like "Resolved by admin...: in favour of seller." from
// its PRIOR resolution. status is the only field the backend's own
// terminal check ever looks at — this must too.
const isDisputeResolved = (dispute) => !isDisputeStatusOpen(dispute?.status);
export const isDisputeOpen = (dispute) => isDisputeStatusOpen(dispute?.status);

// The backend's appeal route (routes/disputes.py::appeal_dispute) sets
// status="Appealed", review_status="appeal_pending" and explicitly re-flags
// the dispute for admin review — it does not touch response_required_from/
// awaiting_response_from/needs_response_from. An appeal must therefore read
// as "waiting on TrustTrade", not fall through to inferring that whichever
// party didn't raise it must respond again.
const isDisputeAppealed = (dispute) => {
  const status = normalizeStatus(dispute?.status, dispute?.review_status);
  return status.includes('appealed') || status.includes('appeal pending');
};

const isDisputeAwaitingReview = (dispute) => {
  const status = normalizeStatus(dispute?.status, dispute?.review_status);
  return isDisputeAppealed(dispute) ||
    ['review', 'under review', 'admin review', 'trusttrade review'].some((value) => status.includes(value));
};

const userSubmittedDisputeResponse = (dispute, role) => {
  if (role === 'Buyer') {
    return Boolean(dispute?.buyer_statement || dispute?.buyer_statement_at || dispute?.buyer_response_at || dispute?.buyer_responded_at);
  }
  if (role === 'Seller') {
    return Boolean(dispute?.seller_statement || dispute?.seller_statement_at || dispute?.seller_response_at || dispute?.seller_responded_at);
  }
  return false;
};

const payoutCompleted = (transaction) =>
  String(transaction?.bank_payout_status || '').trim().toLowerCase() === 'paid';

const getDisputeActorRole = (dispute, transaction) => {
  if (!dispute || !transaction) return null;
  if (dispute.raised_by_user_id && dispute.raised_by_user_id === transaction.buyer_user_id) return 'Buyer';
  if (dispute.raised_by_user_id && dispute.raised_by_user_id === transaction.seller_user_id) return 'Seller';
  if (emailMatches(transaction.buyer_email, { email: dispute.raised_by_email })) return 'Buyer';
  if (emailMatches(transaction.seller_email, { email: dispute.raised_by_email })) return 'Seller';
  return null;
};

const getDisputeActionState = (dispute, transaction, user) => {
  const role = isUserBuyerForTransaction(transaction, user) ? 'Buyer' : isUserSellerForTransaction(transaction, user) ? 'Seller' : 'Viewer';
  if (!isExplicitDisputeCardParticipant(transaction, user)) return { kind: 'hidden', role };
  if (isDisputeResolved(dispute)) {
    return { kind: 'resolved', role, label: 'Dispute resolved', color: D.sub, priority: 90 };
  }
  if (payoutCompleted(transaction)) {
    return { kind: 'resolved', role, label: 'Dispute resolved', color: D.sub, priority: 90 };
  }
  if (!isDisputeOpen(dispute)) return { kind: 'hidden', role };

  const explicitRequired = normalizeStatus(dispute?.response_required_from, dispute?.awaiting_response_from, dispute?.needs_response_from);
  // An explicit response_required_from/awaiting_response_from/needs_response_from
  // always wins, even on an appealed dispute — only when none of those name a
  // party do we show the review-waiting state, and for an appeal specifically
  // that must be "TrustTrade is reviewing the appeal", not a generic dispute
  // message and never an inferred "the other party must respond".
  if (isDisputeAwaitingReview(dispute) && !explicitRequired) {
    return isDisputeAppealed(dispute)
      ? { kind: 'waiting', role, label: 'Appeal under review', statusLabel: 'Appeal under review', noteLabel: 'TrustTrade is reviewing the appeal.', color: D.warn, priority: 80 }
      : { kind: 'waiting', role, label: 'Waiting for TrustTrade review', statusLabel: 'Dispute under review', noteLabel: 'No response is needed from you right now. TrustTrade is reviewing the case.', color: D.warn, priority: 80 };
  }
  const raisedByRole = getDisputeActorRole(dispute, transaction);
  const requiredRole = explicitRequired.includes('buyer') ? 'Buyer' : explicitRequired.includes('seller') ? 'Seller' : raisedByRole === 'Buyer' ? 'Seller' : raisedByRole === 'Seller' ? 'Buyer' : null;

  if (requiredRole === role && !userSubmittedDisputeResponse(dispute, role)) {
    return {
      kind: 'action',
      role,
      label: `${role} response required`,
      color: D.error,
      priority: 10,
    };
  }

  if (isDisputeAwaitingReview(dispute) || userSubmittedDisputeResponse(dispute, role) || requiredRole) {
    return { kind: 'waiting', role, label: 'Waiting for TrustTrade review', statusLabel: 'Dispute under review', noteLabel: 'No response is needed from you right now. TrustTrade is reviewing the case.', color: D.warn, priority: 80 };
  }

  return { kind: 'waiting', role, label: 'Waiting for TrustTrade review', statusLabel: 'Dispute under review', noteLabel: 'No response is needed from you right now. TrustTrade is reviewing the case.', color: D.warn, priority: 80 };
};

export const filterUserRelevantDisputes = (disputes, transactions, user) => {
  const transactionById = new Map(transactions.map((transaction) => [transaction.transaction_id, transaction]));
  return disputes.filter((dispute) => {
    const transaction = transactionById.get(dispute.transaction_id);
    return isUserParticipantForTransaction(transaction, user);
  });
};

// "Protected value" must mean money our payment provider has actually
// confirmed funding for — the UI presentation state alone (e.g. DISPUTED,
// which can be reached before any payment is made) is not proof of that.
// See isProviderFundingConfirmed in transactionState.js.
// Uses the exact same amount basis as the dominant card (buildActionItems'
// `amount`, below — both call getTransactionValue, nothing else). Previously
// this reduced on `t.total || getTransactionValue(t)` while the dominant
// card used getTransactionValue(t) alone (item_price first); for a
// buyer-pays-fee transaction where total = item_price + fees, that made the
// same transaction show two different amounts in two different places on
// the same page. This is the agreed transaction/item value — not a
// provider-fee-inclusive actual — so the metric is labelled "Protected
// transaction value" in the UI, not "amount processed by the provider".
export function computeProtectedValue(transactions, pendingDisputes, now, payoutSchedule) {
  return transactions
    .filter(t =>
      ['ESCROW_LOCKED', 'DELIVERY_PENDING', 'DELIVERED', 'DISPUTED'].includes(resolveEscrowUiState(t, pendingDisputes, now, payoutSchedule).state) &&
      isProviderFundingConfirmed(t)
    )
    .reduce((sum, t) => sum + getTransactionValue(t), 0);
}

export function buildActionItems(transactions, pendingDisputes, user, payoutSchedule) {
  return transactions
    .map((transaction) => {
      const isBuyer = isUserBuyerForTransaction(transaction, user);
      const isSeller = isUserSellerForTransaction(transaction, user);
      // Role must be passed through so role-aware copy (e.g. getPayoutStageCopy's
      // seller vs buyer payout wording) comes back correctly framed for this
      // viewer — without it, meta.label/meta.description always render the
      // buyer-facing variant even when the current user is the seller.
      const meta = resolveEscrowUiState(transaction, pendingDisputes, { role: isSeller ? 'seller' : isBuyer ? 'buyer' : undefined });
      if (transaction.archived || ['EXPIRED', 'CANCELLED', 'REFUNDED', 'COMPLETED'].includes(meta.state)) {
        return null;
      }
      const flowType = getTransactionFlowType(transaction);
      const flow = getFlowCopy(transaction);
      // Authoritative proof the payment provider has actually confirmed
      // funding — never assume "protected"/"held" wording from a UI
      // presentation state alone (e.g. a dispute can be raised before any
      // payment is made). See isProviderFundingConfirmed in
      // transactionState.js.
      const fundingConfirmed = isProviderFundingConfirmed(transaction);
      const scopedDisputes = pendingDisputes.filter((d) => d.transaction_id === transaction.transaction_id);
      const disputeStates = scopedDisputes.map((dispute) => getDisputeActionState(dispute, transaction, user));
      // 'resolved'-kind entries are deliberately not selected here: a
      // resolved dispute can never make meta.state === 'DISPUTED' (see
      // hasOpenDispute in transactionState.js), so a dispute branch below
      // would never fire for one anyway — the dispute gate is on meta.state,
      // not on finding a 'resolved' entry.
      const disputeState = disputeStates.find((state) => state.kind === 'action') ||
        disputeStates.find((state) => state.kind === 'waiting');
      const amount = getTransactionValue(transaction);
      const base = {
        transaction,
        amount,
        role: isBuyer ? 'Buyer' : isSeller ? 'Seller' : 'Viewer',
        status: meta.label,
        path: `/transactions/${transaction.transaction_id}`,
        priority: 99,
        button: 'View Transaction',
        title: 'Review transaction',
        helper: meta.description,
        color: D.accent,
        eyebrow: 'Current step',
      };

      // moneyWord: the word/phrase shown right after the Rand amount in the
      // dominant card's headline (e.g. "R700 protected"). moneyLocation:
      // where the money physically is right now, in plain English that never
      // implies TrustTrade itself holds customer funds, and never says
      // "protected"/"held" unless fundingConfirmed is true. statusPlain: a
      // short restatement of the transaction status. moneyNote: the
      // one-sentence consequence — what this status means for when/whether
      // the money moves. Together these answer the four money-transparency
      // questions before the actor-instruction (title/button) answers "who
      // acts next". Branches below cover both actionable states (the viewer
      // must do something) and important non-actionable ones (money moved
      // or needs attention, but not from the viewer) — only genuinely
      // terminal/irrelevant states are filtered above.

      if (isBuyer && meta.state === 'FUNDED') {
        return {
          ...base, priority: 3, eyebrow: 'Your next step', title: 'Make your payment', status: 'Waiting for your payment', button: 'Pay now',
          color: D.warn, solidBg: D.warn, textColor: D.warnText, helper: meta.secondaryLabel || 'Your payment is held safely until you confirm you received your order.',
          moneyWord: 'awaiting payment', moneyLocation: 'Not paid yet — nothing has moved.', statusPlain: 'Waiting for your payment',
          moneyNote: 'Protection with our payment provider starts as soon as you pay.',
        };
      }
      if (isSeller && meta.state === 'FUNDED') {
        return {
          ...base, priority: 4, eyebrow: 'Waiting on the buyer', title: 'No action needed from you yet', status: "Waiting for the buyer's payment", button: 'View Transaction',
          color: D.warn, helper: meta.secondaryLabel || 'Share this link with the buyer. Funds will be protected once the buyer pays.',
          moneyWord: 'awaiting payment', moneyLocation: 'Not paid yet — still with the buyer.', statusPlain: "Waiting for the buyer's payment",
          moneyNote: 'The money will be protected with our payment provider as soon as the buyer pays.',
        };
      }
      if (isSeller && ['ESCROW_LOCKED', 'DELIVERY_PENDING'].includes(meta.state)) {
        return {
          ...base,
          priority: 5,
          eyebrow: 'Your next step',
          title: flowType === 'delivery' ? 'Add delivery details' : 'Deliver the item to the buyer',
          status: 'Payment protected',
          button: flowType === 'delivery' ? 'Add Delivery Info' : 'View Transaction',
          color: D.success,
          helper: flow.securedSeller,
          moneyWord: fundingConfirmed ? 'protected' : 'awaiting payment confirmation',
          moneyLocation: fundingConfirmed ? 'Held securely with our payment provider.' : 'Payment has not been confirmed with our payment provider yet.',
          statusPlain: meta.state === 'DELIVERY_PENDING' ? 'Delivery in progress' : 'Payment protected',
          moneyNote: fundingConfirmed
            ? "The money will be released to you once the buyer confirms they've received it."
            : 'The money will only be released once payment is confirmed and the buyer confirms receipt.',
        };
      }
      // Buyer during ESCROW_LOCKED/DELIVERY_PENDING: nothing for the buyer
      // to do yet, but they should still see where their money is while
      // they wait for the seller to deliver.
      if (isBuyer && ['ESCROW_LOCKED', 'DELIVERY_PENDING'].includes(meta.state)) {
        return {
          ...base,
          priority: 50,
          eyebrow: 'No action needed',
          title: 'Waiting for the seller to deliver',
          status: 'Payment protected',
          button: 'View Transaction',
          color: D.success,
          helper: flow.securedBuyer,
          moneyWord: fundingConfirmed ? 'protected' : 'awaiting payment confirmation',
          moneyLocation: fundingConfirmed ? 'Held securely with our payment provider.' : 'Payment has not been confirmed with our payment provider yet.',
          statusPlain: meta.state === 'DELIVERY_PENDING' ? 'Delivery in progress' : 'Payment protected',
          moneyNote: "The money will be released to the seller once you confirm you've received it.",
        };
      }
      if (isBuyer && meta.state === 'DELIVERED' && meta.actionable) {
        return {
          ...base, priority: 6, eyebrow: 'Your next step', title: flow.confirmationLabel, button: flow.confirmAction, color: D.success, helper: flow.confirmationDescription,
          moneyWord: fundingConfirmed ? 'protected' : 'awaiting payment confirmation',
          moneyLocation: fundingConfirmed ? 'Held securely with our payment provider.' : 'Payment has not been confirmed with our payment provider yet.',
          statusPlain: 'Waiting for you to confirm receipt',
          moneyNote: "The money will be released to the seller once you confirm you've received it.",
        };
      }
      // Seller during the same (buyer hasn't confirmed receipt yet):
      // nothing for the seller to do, but still worth showing where the
      // money is while they wait.
      if (isSeller && meta.state === 'DELIVERED' && meta.actionable) {
        return {
          ...base,
          priority: 51,
          eyebrow: 'No action needed',
          title: 'Waiting for the buyer to confirm receipt',
          status: 'Awaiting buyer confirmation',
          button: 'View Transaction',
          color: D.success,
          helper: flow.securedSeller,
          moneyWord: fundingConfirmed ? 'protected' : 'awaiting payment confirmation',
          moneyLocation: fundingConfirmed ? 'Held securely with our payment provider.' : 'Payment has not been confirmed with our payment provider yet.',
          statusPlain: 'Waiting for the buyer to confirm receipt',
          moneyNote: 'The money will be released to you once the buyer confirms receipt.',
        };
      }
      // DELIVERED but not actionable: the buyer has already confirmed
      // receipt and the transaction is waiting on the provider to actually
      // release funds. meta.label/meta.description already carry the
      // correct copy here (getConfirmedAwaitingReleaseCopy, computed inside
      // resolveEscrowUiState) — reuse it rather than re-deriving.
      if (meta.state === 'DELIVERED' && !meta.actionable) {
        return {
          ...base,
          priority: 55,
          eyebrow: 'No action needed',
          title: meta.label,
          status: meta.label,
          button: 'View Transaction',
          color: D.success,
          helper: meta.description,
          moneyWord: fundingConfirmed ? 'protected' : 'awaiting payment confirmation',
          moneyLocation: fundingConfirmed ? 'Held securely with our payment provider.' : 'Payment has not been confirmed with our payment provider yet.',
          statusPlain: meta.label,
          moneyNote: meta.description,
        };
      }
      // Authoritative financial states are checked BEFORE dispute
      // presentation. resolveEscrowUiState itself already resolves RELEASED/
      // PAYOUT_REVIEW ahead of DISPUTED internally (a transaction whose
      // money has actually left the provider is never classified as
      // meta.state === 'DISPUTED', even if it has an old resolved dispute
      // record) — but the dispute branches below previously fired purely
      // from disputeState.kind, independent of meta.state. That let a
      // resolved-dispute record "win" the dominant card over an
      // authoritative RELEASED/PAYOUT_REVIEW state and incorrectly render
      // "protected"/"held with our payment provider" for money that had
      // already left the provider. Gating every dispute branch on
      // meta.state === 'DISPUTED' — the actual current blocking state, not
      // dispute history — fixes that: once money is released, meta.state
      // can never be DISPUTED, so these branches are structurally unable to
      // override it, and a resolved-dispute-only record (which also can
      // never produce meta.state === 'DISPUTED' — see hasOpenDispute in
      // transactionState.js) now correctly falls through to whatever the
      // transaction's real current state is instead of showing stale
      // "Dispute resolved" history as if it still controlled the money.
      if (meta.state === 'RELEASED') {
        return {
          ...base, priority: 70, title: 'No action needed', status: meta.label, button: 'View Transaction', color: D.warn, eyebrow: 'No action needed', helper: meta.description,
          moneyWord: 'released', moneyLocation: isSeller ? 'On its way to your bank account.' : 'Released to the seller.', statusPlain: meta.label,
          moneyNote: meta.description || payoutSchedule.shortCopy || 'Bank transfers can take up to 2 business days.',
        };
      }
      // Funds were released but the bank payout itself needs attention —
      // the money has left the provider, so it is never "protected with
      // our payment provider" here; it needs TrustTrade/support attention,
      // not the customer's.
      if (meta.state === 'PAYOUT_REVIEW') {
        return {
          ...base, priority: 20, title: 'No action needed', status: meta.label, button: 'View Transaction', color: D.error, eyebrow: 'Status update', helper: meta.description,
          moneyWord: 'released', moneyLocation: 'Released, but your bank payout needs attention.', statusPlain: meta.label,
          moneyNote: meta.description,
        };
      }
      if (meta.state === 'DISPUTED' && disputeState?.kind === 'action') {
        return {
          ...base, priority: disputeState.priority, eyebrow: 'Your next step', title: disputeState.label, status: disputeState.label, button: 'Respond to dispute', path: '/disputes-dashboard', color: D.error, helper: 'Your response is needed before TrustTrade can continue the review.',
          moneyWord: fundingConfirmed ? 'protected' : 'in dispute',
          moneyLocation: fundingConfirmed ? 'Held securely with our payment provider.' : 'Not paid yet — no money has moved.',
          statusPlain: 'Dispute under review',
          moneyNote: fundingConfirmed
            ? 'The money will not be released while this dispute is being reviewed.'
            : 'No payment has been made, so there is nothing to release while this is reviewed.',
        };
      }
      if (meta.state === 'DISPUTED' && disputeState?.kind === 'waiting') {
        // disputeState.statusLabel/noteLabel distinguish an appealed dispute
        // ("Appeal under review" / "TrustTrade is reviewing the appeal")
        // from a regular one awaiting review ("Dispute under review" /
        // "...reviewing the case") — see getDisputeActionState above. Never
        // infer that a specific party must respond here; this branch only
        // fires when no response_required_from/etc. explicitly names one.
        return {
          ...base, priority: disputeState.priority, title: 'No action needed from you right now', status: disputeState.label, button: 'View Dispute', path: '/disputes-dashboard', color: D.warn, eyebrow: 'No action needed', helper: disputeState.noteLabel,
          moneyWord: fundingConfirmed ? 'protected' : 'in dispute',
          moneyLocation: fundingConfirmed ? 'Held securely with our payment provider.' : 'Not paid yet — no money has moved.',
          statusPlain: disputeState.statusLabel,
          moneyNote: fundingConfirmed
            ? `${disputeState.noteLabel} The money will not be released until this is resolved.`
            : `${disputeState.noteLabel} No payment has been made, so there is nothing to release yet.`,
        };
      }
      return null;
    })
    .filter(Boolean)
    .sort((a, b) => a.priority - b.priority);
}
